import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { setSessionTimeoutWarning } from '../../actions';
import { formatLeft, getSessionTimeoutLeftMs, getSessionTimeoutWarning } from '../../functions';
const useStyles = makeStyles()(() => {
    return {
        root: {
            position: 'fixed',
            inset: 0,
            zIndex: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // 画面全体は覆うが、暗くしすぎない。会議は続いている。
            background: 'rgba(0,0,0,.42)',
            fontFamily: 'var(--font, system-ui, sans-serif)'
        },
        card: {
            maxWidth: 420,
            padding: '28px 30px',
            color: 'var(--text, #eaf0ff)',
            background: 'var(--surface, rgba(20,28,54,.55))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius, 22px)',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            backdropFilter: 'blur(18px) saturate(140%)'
        },
        title: {
            fontFamily: 'var(--font-head, var(--font, system-ui, sans-serif))',
            fontSize: 20,
            fontWeight: 600,
            margin: '0 0 12px'
        },
        body: {
            fontSize: 15,
            lineHeight: 1.9,
            margin: 0
        },
        left: {
            color: 'var(--danger, #D83848)',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums'
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 22
        },
        button: {
            padding: '11px 22px',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 700,
            color: 'var(--on-accent, #fff)',
            background: 'var(--accent, #246FE5)',
            border: 0,
            borderRadius: 'var(--radius-sm, 12px)'
        }
    };
});
/**
 * 退出の予告。
 *
 * 予告なしに画面が変わると、利用者は不具合と区別できない。
 *
 * 「続ける」を出すのは無操作(idle)のときだけ。上限(max)は延長させない
 * ため、残り時間だけを見せる。ここにボタンを出すと「30分以上は付けない」
 * が守れなくなる。
 *
 * @returns {JSX.Element|null}
 */
export default function SessionTimeoutWarning() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const reason = useSelector(getSessionTimeoutWarning);
    const leftMs = useSelector(getSessionTimeoutLeftMs);
    /*
     * 「続ける」は操作そのもの。onClick が動く前に document の pointerdown を
     * 拾って無操作タイマーが戻るので、ここでは予告を閉じるだけでよい。
     * 閉じずに放っておいても次の tick が閉じるが、押した手応えは即座に返す。
     */
    const onContinue = useCallback(() => {
        dispatch(setSessionTimeoutWarning(null));
    }, [dispatch]);
    if (!reason || leftMs === null) {
        return null;
    }
    return (React.createElement("div", { className: classes.root },
        React.createElement("div", { className: classes.card },
            React.createElement("h2", { className: classes.title }, 'まもなく退出します'),
            React.createElement("p", { className: classes.body },
                reason === 'max'
                    ? '利用できる時間の上限に達します。'
                    : '操作がないため、まもなく退出します。',
                React.createElement("br", null),
                'あと ',
                React.createElement("span", { className: classes.left }, formatLeft(leftMs)),
                ' です。'),
            reason === 'idle' && (React.createElement("div", { className: classes.actions },
                React.createElement("button", { className: classes.button, onClick: onContinue, type: 'button' }, '続ける'))))));
}
